import dynamic from 'next/dynamic';

import { useSetAtom } from 'jotai';

import { useUserGroupRestriction } from '@/utils/hooks/acl/useUserGroupRestriction';
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useAcl from '@/utils/hooks/user/useAcl';

import { useProfilesStore } from '@/utils/zustand/providers/profiles-provider';
import useToggle from '@/utils/hooks/utils/useToggle';

import type { SocialProfileType } from '@/types/socialProfileType';

import { aclFeatures } from '@/lib/acl/aclFeatures';

import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon/index';
import { TooltipPlanRestriction } from '@/components/layouts/LayoutPlatform/components/Menu/components/TooltipPlanRestriction';
const SaveSelectedProfilesAsGroupModal = dynamic(() =>
    import('@/components/composite/SpGroups/SaveSelectedProfilesAsGroupModal').then(
        (mod) => mod.SaveSelectedProfilesAsGroupModal
    )
);
import { ModalUpsell } from '@/components/composite/Modals/ModalUpsell';
import { menuOverlayAtom } from '@/components/layouts/LayoutPlatform/components/Menu/menuOverlayAtom';

export function CreateGroupButton() {
    const { t } = useTranslation();
    const { isOpen, toggle } = useToggle<SocialProfileType[]>();

    const { hasGroupPermission } = useUserGroupRestriction();
    const { activatedSocialProfiles } = useSocialProfiles({ revalidateIfStale: false });
    const { activeMultiSelectionProfiles } = useProfilesStore((state) => state);
    const setMenuOverlay = useSetAtom(menuOverlayAtom);

    const { hasAcl } = useAcl();

    const requiresPlanAbove = aclFeatures.group.base.read && !hasAcl(aclFeatures.group.base.read);

    return (
        <>
            <div>
                <TooltipPlanRestriction
                    position="top"
                    disabled={hasGroupPermission}
                    className="flex flex-row">
                    <span className="inline-block">
                        <Button
                            data-testid="create-group-button"
                            onClick={(event) => {
                                if (!hasAcl(aclFeatures.group.base.read)) {
                                    event.preventDefault();
                                    setMenuOverlay(
                                        <ModalUpsell
                                            heading={t('commons:upsell_modal.group.heading')}
                                            description={t(
                                                'commons:upsell_modal.group.description'
                                            )}
                                            benefits={[
                                                {
                                                    text: t('commons:upsell_modal.group.benefit1'),
                                                    icon: 'check'
                                                },
                                                {
                                                    text: t('commons:upsell_modal.group.benefit2'),
                                                    icon: 'check'
                                                },
                                                {
                                                    text: t('commons:upsell_modal.group.benefit3'),
                                                    icon: 'check'
                                                },
                                                {
                                                    text: t('commons:upsell_modal.group.benefit4'),
                                                    icon: 'plus'
                                                }
                                            ]}
                                            illustrationPath="/images/modals/modalUpsell/group.png"
                                            dismiss={() => setMenuOverlay('')}
                                        />
                                    );
                                } else {
                                    toggle();
                                }
                            }}
                            variant={requiresPlanAbove ? 'light' : 'primary'}
                            icon="plus">
                            {t('layout_platform:empty_states.multi_profiles.button')}
                            {requiresPlanAbove ? (
                                <Icon icon="crown" className="ml-3 text-icoBlue" />
                            ) : null}
                        </Button>
                    </span>
                </TooltipPlanRestriction>
            </div>
            <SaveSelectedProfilesAsGroupModal
                isOpen={isOpen}
                toggle={toggle}
                selectedProfiles={activeMultiSelectionProfiles?.socialProfileIds?.map(
                    (id) => activatedSocialProfiles?.find((profile) => profile.id === id)
                )}
            />
        </>
    );
}
